import React, { useState, useEffect, useCallback } from "react";
import { useClerk } from "@clerk/clerk-react";
import { Box, Text, HStack, Button, Icon, CloseButton } from "@chakra-ui/react";
import { VscWarning, VscAccount } from "react-icons/vsc";
import { useSessionStore } from "../store";
import { track, trackWithAttribution, Events } from "../utils/analytics";

/**
 * GuestSessionWarning Component
 *
 * Displays a warning banner for guest users about their session expiration.
 * Shows a countdown timer and prompts users to sign up to save their work.
 *
 * @param {string} roomId - The current room ID (used for redirect after signup)
 * @param {Date|number} expiresAt - When the room will expire (Date object or Unix timestamp in seconds)
 */
export default function GuestSessionWarning({ roomId, expiresAt }) {
  const { redirectToSignUp } = useClerk();
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [isDismissed, setIsDismissed] = useState(() => sessionStorage.getItem("guest_warning_dismissed") === "true");
  const [isUrgent, setIsUrgent] = useState(false);

  // Calculate time remaining
  const calculateTimeRemaining = useCallback(() => {
    if (!expiresAt) return null;

    // Handle both Unix timestamp (seconds) and Date objects
    const expiryTime = typeof expiresAt === 'number'
      ? expiresAt * 1000 // Convert seconds to milliseconds
      : expiresAt.getTime();

    const now = Date.now();
    const diff = expiryTime - now;

    if (diff <= 0) {
      return { hours: 0, minutes: 0, seconds: 0, expired: true };
    }

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diff % (1000 * 60)) / 1000);

    return { hours, minutes, seconds, expired: false };
  }, [expiresAt]);

  // Update timer every second
  useEffect(() => {
    const updateTimer = () => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);

      // Set urgent state when less than 1 hour remaining
      if (remaining && !remaining.expired) {
        setIsUrgent(remaining.hours < 1);
      }
    };

    // Initial calculation
    updateTimer();

    // Update every second
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [calculateTimeRemaining]);

  // Handle sign up click
  const handleSignUp = () => {
    window.__AUTH_INTENT__ = 'signup';
    track(Events.GUEST_SIGNUP_CLICKED, { roomId });
    const posthogEnabled = typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function';
    console.log('[Analytics] guest_signup_clicked tracking', { roomId, posthogEnabled });
    trackWithAttribution(Events.SIGN_UP_STARTED, { roomId, source: 'guest_warning' });
    console.log('[Analytics] sign_up_started (guest_warning) tracking', { roomId, posthogEnabled });
    // CRITICAL: Preserve session ID before Clerk redirect
    // This ensures guest rooms can be migrated after sign-up
    const preserveSession = useSessionStore.getState().preserveSessionForAuth;
    preserveSession();
    const dest = roomId ? `/editor/room-settings/${roomId}` : '/my-rooms';
    useSessionStore.getState().setPendingAuthRedirect(dest);
    console.log('[AUTH] Session preserved before sign-up redirect from GuestSessionWarning');

    redirectToSignUp({
      redirectUrl: dest,
      afterSignUpUrl: dest
    });
  };

  // Format time for display
  const formatTime = () => {
    if (!timeRemaining) return "calculating...";
    if (timeRemaining.expired) return "expired";

    const { hours, minutes } = timeRemaining;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m ${timeRemaining.seconds}s`;
  };

  // Don't render if dismissed or no expiry time
  if (isDismissed || !expiresAt) {
    return null;
  }

  // Don't render if expired (room will be deleted soon)
  if (timeRemaining?.expired) {
    return (
      <Box
        position="fixed"
        top="60px"
        left="50%"
        transform="translateX(-50%)"
        zIndex="10001"
        bg="red.600"
        color="white"
        px={4}
        py={3}
        borderRadius="md"
        boxShadow="lg"
        maxW="500px"
        w="90%"
      >
        <HStack spacing={3}>
          <Icon as={VscWarning} boxSize={5} />
          <Text fontWeight="bold" fontSize="sm">
            Session expired - your work may be lost. Sign up now to save it.
          </Text>
          <Button
            size="sm"
            colorScheme="white"
            variant="outline"
            onClick={handleSignUp}
            _hover={{ bg: "whiteAlpha.200" }}
          >
            Sign Up
          </Button>
        </HStack>
      </Box>
    );
  }

  return (
    <Box
      position="fixed"
      top="60px"
      left="50%"
      transform="translateX(-50%)"
      zIndex="10001"
      bg={isUrgent ? "orange.500" : "blue.600"}
      color="white"
      px={4}
      py={2}
      borderRadius="md"
      boxShadow="lg"
      maxW="600px"
      w="90%"
      transition="background-color 0.3s"
    >
      <HStack spacing={3} justify="space-between">
        <HStack spacing={2} flex={1}>
          <Icon as={isUrgent ? VscWarning : VscAccount} boxSize={5} />
          <Text fontSize="sm" fontWeight="medium">
            {isUrgent ? (
              <>⏰ Guest session expires in <strong>{formatTime()}</strong> - sign up to keep your room!</>
            ) : (
              <>You're editing as a guest. Your room will be saved for <strong>{formatTime()}</strong>.</>
            )}
          </Text>
        </HStack>

        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme={isUrgent ? "yellow" : "green"}
            onClick={handleSignUp}
            fontWeight="bold"
          >
            Sign Up Free
          </Button>

          {!isUrgent && (
            <CloseButton
              size="sm"
              onClick={() => { sessionStorage.setItem("guest_warning_dismissed", "true"); setIsDismissed(true); }}
              _hover={{ bg: "whiteAlpha.200" }}
            />
          )}
        </HStack>
      </HStack>
    </Box>
  );
}
